// Loads the user's sites from the backend and maps them to the shape the
// Claude Design shell expects: { id, site_id, name, url, urlFull, flag, lang }.
// SaaS (signed in) → GET /api/account/sites; legacy/auth-off → GET /api/wp/sites.
// Returns [] on any failure so the caller can fall back to mock data in dev.

import { getDomain } from 'tldts';
import { api } from './api.js';

// host without protocol / www / trailing slash, for display
function hostOf(url) {
  return String(url || '').replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
}

// Derive a flag + language from the TLD so the shell's site picker looks right
// (the backend doesn't carry these). Falls back to a globe + English.
const TLD = {
  'co.uk': ['🇬🇧', 'English (UK)'], 'com.au': ['🇦🇺', 'English (AU)'], 'co.nz': ['🇳🇿', 'English (NZ)'],
  uk: ['🇬🇧', 'English (UK)'], de: ['🇩🇪', 'German'], fr: ['🇫🇷', 'French'], es: ['🇪🇸', 'Spanish'],
  it: ['🇮🇹', 'Italian'], nl: ['🇳🇱', 'Dutch'], pt: ['🇵🇹', 'Portuguese'], br: ['🇧🇷', 'Portuguese (BR)'],
  ca: ['🇨🇦', 'English (CA)'], au: ['🇦🇺', 'English (AU)'], se: ['🇸🇪', 'Swedish'], pl: ['🇵🇱', 'Polish'],
  jp: ['🇯🇵', 'Japanese'], in: ['🇮🇳', 'English (IN)'], us: ['🇺🇸', 'English (US)'], com: ['🇺🇸', 'English (US)'],
};
function flagLang(host) {
  const parts = hostOf(host).split('.');
  const two = parts.slice(-2).join('.');           // e.g. "co.uk"
  const one = parts.slice(-1)[0];                   // e.g. "fr"
  return TLD[two] || TLD[one] || ['🌐', 'English'];
}

// Registrable domain (eTLD+1) via the full Public Suffix List: the client
// mirror of backend/lib/properties.js rootDomainOf (same tldts library and
// options, so grouping keys always agree). Used as the property grouping key
// when the backend didn't send one (legacy /wp/sites, dev mocks). IPs /
// localhost / single-label hosts fall back to the host itself.
export function rootDomainOf(url) {
  const host = hostOf(url).split('/')[0].split(':')[0];
  if (!host) return '';
  return getDomain(host, { allowPrivateDomains: true }) || host;
}

function mapSite(raw) {
  const site_id = raw.site_id != null ? raw.site_id : raw.id;
  const host = hostOf(raw.url);
  const [flag, lang] = flagLang(host);
  const property_root = raw.property_root || rootDomainOf(raw.url);
  return {
    id: site_id,                 // matches the shell's active-id usage + api site_id scoping
    site_id,
    name: raw.name || host,
    url: host,                   // display
    urlFull: raw.url || ('https://' + host),
    flag,
    lang,
    wp_type: raw.wp_type || 'single',   // 'single' | 'network'
    // What backs this site. 'wordpress' and 'siteatlas' name which companion
    // plugin is behind it and both can be published to; 'crawl' is a public
    // site with NO CMS at all, which MarketLens reads by crawling and can only
    // ever measure. Screens that write must branch on this, not assume it.
    source: raw.source || 'wordpress',  // 'wordpress' | 'siteatlas' | 'crawl'
    property_id: raw.property_id != null ? raw.property_id : null,
    property_root,               // grouping key for the in-app switcher
    property_name: raw.property_name || property_root,
    over: false,
  };
}

export async function loadSites(me) {
  try {
    if (me && me.authenticated) {
      const data = await api('/account/sites');
      return (data && Array.isArray(data.sites) ? data.sites : []).map(mapSite);
    }
    const data = await api('/wp/sites');
    return (data && Array.isArray(data.sites) ? data.sites : []).map(mapSite);
  } catch {
    return [];
  }
}

// The account's properties (domain-root groups): [{ id, root_domain, name,
// primary_site_id, primary_explicit, sites: [{ site_id, url, name }] }].
export async function loadProperties() {
  try {
    const data = await api('/account/properties');
    return (data && Array.isArray(data.properties)) ? data.properties : [];
  } catch {
    return [];
  }
}
